import React, { useEffect, useLayoutEffect, useState } from "react";
import GuidedTour from "../../components/GuidedTour";
import { tourStepsMultipleWords } from "./tourStepsMultipleWords";
import { useTour } from "../../context/TourContext";
import { useNavigate } from "react-router-dom";
import BubbleText from "../../components/BubbleText";
import GamePageToolbar from "../../components/GamePageToolbar";
import {
  Collection,
  GamePair,
  getGameCollections,
  saveGameCollection,
  updateGameCollection,
  deleteCollection,
} from "../../api";
import { useAuth0 } from "@auth0/auth0-react";

interface WordPair {
  id: string;
  prompt: string;
  match: string;
}

const createPairId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `pair-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const shufflePairs = (pairs: WordPair[]) => {
  const next = [...pairs];
  for (let index = next.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
  }
  return next;
};

const parseSavedPairs = (description: string | GamePair[]): WordPair[] => {
  let items: GamePair[] = [];
  if (typeof description === "string") {
    const firstParse: unknown = JSON.parse(description);
    items =
      typeof firstParse === "string" ? JSON.parse(firstParse) : firstParse as GamePair[];
  } else {
    items = description;
  }

  return items
    .map((item) => ({
      id: createPairId(),
      prompt: item.word?.trim() ?? "",
      match: item.imageUrl?.trim() ?? "",
    }))
    .filter((pair) => pair.prompt.length > 0 && pair.match.length > 0);
};

const MultipleWordsGame: React.FC = () => {
  const { isGuidedTourEnabled, isTourRunning, setIsTourRunning } = useTour();
  const [currentTourStep, setCurrentTourStep] = useState(0);
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();

  const [isGameStarted, setIsGameStarted] = useState(false);
  const [isGameCompleted, setIsGameCompleted] = useState(false);
  const [customPairs, setCustomPairs] = useState<WordPair[]>([]);
  const [shuffledMatches, setShuffledMatches] = useState<WordPair[]>([]);
  const [newPrompt, setNewPrompt] = useState("");
  const [newMatch, setNewMatch] = useState("");
  const [connections, setConnections] = useState<Record<string, string>>({});
  const [selectedPromptId, setSelectedPromptId] = useState<string | null>(null);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(60);
  const [timerInput, setTimerInput] = useState(60);
  const [isTimedMode, setIsTimedMode] = useState(true);
  const [finalCompletionTime, setFinalCompletionTime] = useState<number | null>(
    null,
  );
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [savedCollections, setSavedCollections] = useState<Collection[]>([]);
  const [collectionName, setCollectionName] = useState("");
  const [lineCoords, setLineCoords] = useState<
    { pairId: string; x1: number; y1: number; x2: number; y2: number }[]
  >([]);

  const handleTourComplete = () => {
    setIsTourRunning(false);
    localStorage.setItem("tourCompleted_multipleWords", "true");
  };

  const resetPlayState = () => {
    setConnections({});
    setSelectedPromptId(null);
    setScore(0);
    setIsGameCompleted(false);
    setFinalCompletionTime(null);
    setFeedbackMessage("");
    setTimeLeft(isTimedMode ? timerInput : Infinity);
  };

  const startGame = () => {
    if (customPairs.length === 0) {
      return;
    }
    resetPlayState();
    setShuffledMatches(shufflePairs(customPairs));
    setIsGameStarted(true);
  };

  const handleAddPair = () => {
    const prompt = newPrompt.trim();
    const match = newMatch.trim();
    if (!prompt || !match) {
      return;
    }

    setCustomPairs((currentPairs) => [
      ...currentPairs,
      { id: createPairId(), prompt, match },
    ]);
    setNewPrompt("");
    setNewMatch("");
  };

  const handleRemovePair = (idToRemove: string) => {
    setCustomPairs((currentPairs) =>
      currentPairs.filter((pair) => pair.id !== idToRemove),
    );
  };

  const completeGameIfNeeded = (matchedCount: number) => {
    if (matchedCount === customPairs.length && customPairs.length > 0) {
      setIsGameCompleted(true);
      if (isTimedMode) {
        setFinalCompletionTime(timerInput - timeLeft);
      }
    }
  };

  const tryConnect = (promptId: string, matchId: string) => {
    if (isGameCompleted || connections[promptId]) {
      return;
    }

    if (promptId !== matchId) {
      setFeedbackMessage("Incorrect match! Try again.");
      window.setTimeout(() => setFeedbackMessage(""), 2000);
      setSelectedPromptId(null);
      return;
    }

    const nextConnections = {
      ...connections,
      [promptId]: matchId,
    };
    setConnections(nextConnections);
    setScore(Object.keys(nextConnections).length);
    completeGameIfNeeded(Object.keys(nextConnections).length);
    setSelectedPromptId(null);
    setFeedbackMessage("");
  };

  const handlePromptClick = (pairId: string) => {
    if (connections[pairId] || isGameCompleted) {
      return;
    }
    setSelectedPromptId(pairId);
  };

  const handleMatchClick = (pairId: string) => {
    if (!selectedPromptId || isGameCompleted) {
      return;
    }
    tryConnect(selectedPromptId, pairId);
  };

  const handleDragStart = (
    event: React.DragEvent<HTMLDivElement>,
    pairId: string,
  ) => {
    event.dataTransfer.setData("text/plain", pairId);
  };

  const handleDrop = (
    event: React.DragEvent<HTMLDivElement>,
    matchId: string,
  ) => {
    event.preventDefault();
    const promptId = event.dataTransfer.getData("text/plain");
    if (promptId) {
      tryConnect(promptId, matchId);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleBack = () => {
    navigate("/games");
  };

  const goBackToSetup = () => {
    resetPlayState();
    setIsGameStarted(false);
  };

  const resetGame = () => {
    resetPlayState();
    setShuffledMatches(shufflePairs(customPairs));
  };

  useEffect(() => {
    if (isGameStarted && isTimedMode && timeLeft > 0 && !isGameCompleted) {
      const timer = window.setInterval(() => {
        setTimeLeft((currentTime) =>
          currentTime === Infinity ? currentTime : currentTime - 1,
        );
      }, 1000);
      return () => window.clearInterval(timer);
    }
    if (timeLeft === 0) {
      setIsGameCompleted(true);
    }
  }, [isGameStarted, timeLeft, isGameCompleted]);

  useLayoutEffect(() => {
    if (!isGameStarted) {
      setLineCoords([]);
      return;
    }

    const updateLineCoords = () => {
      const next = Object.keys(connections).flatMap((pairId) => {
        const sourceElement = document.getElementById(`source-${pairId}`);
        const targetElement = document.getElementById(`target-${pairId}`);
        if (!sourceElement || !targetElement) {
          return [];
        }

        const sourceRect = sourceElement.getBoundingClientRect();
        const targetRect = targetElement.getBoundingClientRect();
        const gameBoard = document
          .querySelector(".game-board")
          ?.getBoundingClientRect();
        if (!gameBoard) {
          return [];
        }

        return [
          {
            pairId,
            x1: sourceRect.right - gameBoard.left,
            y1: sourceRect.top - gameBoard.top + sourceRect.height / 2,
            x2: targetRect.left - gameBoard.left,
            y2: targetRect.top - gameBoard.top + targetRect.height / 2,
          },
        ];
      });
      setLineCoords(next);
    };

    updateLineCoords();
    window.addEventListener("resize", updateLineCoords);
    return () => window.removeEventListener("resize", updateLineCoords);
  }, [connections, isGameStarted, customPairs, shuffledMatches]);

  const loadSavedCollections = async () => {
    try {
      const collections = await getGameCollections(
        "matching_game",
        getAccessTokenSilently,
      );
      return collections;
    } catch (error) {
      console.error("Failed to load collections:", error);
    }
  };

  useEffect(() => {
    loadSavedCollections().then((collections) => {
      if (collections) {
        setSavedCollections(collections);
      }
    });
  }, []);

  const toGamePairs = (): GamePair[] =>
    customPairs.map((pair) => ({
      word: pair.prompt,
      imageUrl: pair.match,
    }));

  const handleSaveCollection = async () => {
    if (customPairs.length === 0) {
      alert("Please add some pairs before saving!");
      return;
    }
    if (!collectionName.trim()) {
      alert("Please enter a name for your collection!");
      return;
    }

    try {
      await saveGameCollection(
        collectionName,
        toGamePairs(),
        "matching_game",
        getAccessTokenSilently,
      );
      alert("Collection saved successfully!");
      const collections = await loadSavedCollections();
      if (collections) {
        setSavedCollections(collections);
      }
    } catch (error) {
      console.error("Failed to save collection:", error);
      alert("Failed to save collection. Please try again.");
    }
  };

  const isImageValue = (value: string) =>
    value.startsWith("data:image") || value.startsWith("http");

  return (
    <div className={`page-container text-theme-text`}>
      <GamePageToolbar onBack={handleBack}>
        {isGameStarted && (
          <>
            <button
              type="button"
              onClick={resetGame}
              className="reset-button max-w-[300px] cursor-pointer rounded border border-black bg-yellow-500 px-3 py-2 text-base font-bold uppercase text-black transition duration-300 hover:scale-105 hover:bg-yellow-600 active:scale-95 active:bg-yellow-700"
            >
              Reset Game
            </button>
            <button
              type="button"
              onClick={goBackToSetup}
              className="back-to-setup-button max-w-[300px] cursor-pointer rounded border border-black bg-blue-500 px-3 py-2 text-base font-bold uppercase text-white transition duration-300 hover:bg-blue-600"
            >
              Back to Setup
            </button>
          </>
        )}
      </GamePageToolbar>
      <h1 className="mb-8 text-3xl font-bold">
        <BubbleText>Multiple Words Game</BubbleText>
      </h1>

      {!isGameStarted ? (
        <div className="game-instructions flex flex-col items-center">
          <p className="inherit mb-4 text-center">
            Add pairs of related words, like Furniture and Couch. They do not
            have to be the same word.
          </p>
          <div className="mb-4 flex flex-col gap-2 sm:flex-row">
            <input
              type="text"
              value={newPrompt}
              onChange={(event) => setNewPrompt(event.target.value)}
              placeholder="Furniture"
              className="rounded border border-gray-300 p-2"
            />
            <input
              type="text"
              value={newMatch}
              onChange={(event) => setNewMatch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  handleAddPair();
                }
              }}
              placeholder="Couch"
              className="rounded border border-gray-300 p-2"
            />
            <button
              type="button"
              onClick={handleAddPair}
              disabled={!newPrompt.trim() || !newMatch.trim()}
              className="rounded bg-green-500 px-4 py-2 text-white hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Add Pair
            </button>
          </div>

          <div className="mb-4 flex max-w-[700px] flex-col items-center gap-2">
            {customPairs.map((pair) => (
              <div
                key={pair.id}
                className="flex w-full items-center justify-between rounded bg-gray-100 px-3 py-2 text-black"
              >
                <span>
                  {pair.prompt} → {pair.match}
                </span>
                <button
                  type="button"
                  onClick={() => handleRemovePair(pair.id)}
                  className="ml-3 text-red-500 hover:text-red-700"
                  aria-label={`Remove ${pair.prompt} pair`}
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          <p className="inherit mt-2">
            Enter the timer length in seconds for the game:
          </p>
          <input
            type="number"
            value={timerInput}
            onChange={(event) => setTimerInput(Number(event.target.value))}
            className="mt-2 rounded border border-gray-300 p-2"
            placeholder="Enter timer length in seconds"
          />
          <label className="mt-2 flex items-center">
            <input
              type="checkbox"
              checked={!isTimedMode}
              onChange={() => setIsTimedMode((previous) => !previous)}
              className="mr-2"
            />
            No Time Limit
          </label>
          <button
            type="button"
            onClick={startGame}
            disabled={customPairs.length === 0}
            className={`start-button mt-5 max-w-[300px] rounded border border-black bg-green-500 p-2.5 text-base font-bold uppercase text-white transition duration-300 hover:bg-green-600 ${
              customPairs.length === 0
                ? "cursor-not-allowed opacity-50"
                : "cursor-pointer"
            }`}
          >
            Start Game
          </button>

          <div className="mt-4 flex w-full max-w-md flex-col items-center gap-3">
            <div className="flex w-full flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-center">
              <input
                type="text"
                value={collectionName}
                onChange={(event) => setCollectionName(event.target.value)}
                placeholder="Enter collection name"
                className="w-full rounded border p-2 sm:w-auto"
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    handleSaveCollection();
                  }
                }}
              />
              <button
                type="button"
                onClick={handleSaveCollection}
                className="rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
              >
                Save Collection
              </button>
            </div>
            <select
              title="Select a saved collection"
              onChange={(event) => {
                const value = event.target.value;
                if (!value) {
                  setCustomPairs([]);
                  setCollectionName("");
                  resetPlayState();
                  return;
                }
                const collection = savedCollections.find(
                  (saved) => saved.collection_id === Number(value),
                );
                if (!collection) {
                  return;
                }
                try {
                  const pairs = parseSavedPairs(collection.description);
                  if (pairs.length === 0) {
                    throw new Error("No valid pairs found in collection");
                  }
                  setCustomPairs(pairs);
                  setCollectionName(collection.name);
                  resetPlayState();
                } catch (error) {
                  alert(
                    "Error loading collection: " +
                      (error instanceof Error ? error.message : "Unknown error"),
                  );
                }
              }}
              className="w-full rounded border p-2"
            >
              <option value="">Select a saved collection</option>
              {savedCollections.map((collection) => (
                <option
                  key={collection.collection_id}
                  value={collection.collection_id}
                >
                  {collection.name}
                </option>
              ))}
            </select>
            {savedCollections.length > 0 && (
              <div className="flex flex-wrap justify-center gap-2">
                <button
                  type="button"
                  onClick={async () => {
                    const selected = savedCollections.find(
                      (saved) => saved.name === collectionName,
                    );
                    if (!selected) {
                      return;
                    }
                    if (
                      !window.confirm(
                        "Are you sure you want to update this collection?",
                      )
                    ) {
                      return;
                    }
                    try {
                      await updateGameCollection(
                        selected.collection_id,
                        collectionName,
                        toGamePairs(),
                        getAccessTokenSilently,
                        "matching_game",
                      );
                      const collections = await loadSavedCollections();
                      if (collections) {
                        setSavedCollections(collections);
                      }
                      alert("Collection updated successfully!");
                    } catch (error) {
                      console.error("Failed to update collection:", error);
                      alert("Failed to update collection. Please try again.");
                    }
                  }}
                  className="rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
                >
                  Update Selected
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    const selected = savedCollections.find(
                      (saved) => saved.name === collectionName,
                    );
                    if (
                      selected &&
                      window.confirm(
                        "Are you sure you want to delete this collection?",
                      )
                    ) {
                      try {
                        await deleteCollection(
                          selected.collection_id,
                          getAccessTokenSilently,
                        );
                        const collections = await loadSavedCollections();
                        if (collections) {
                          setSavedCollections(collections);
                        }
                        setCollectionName("");
                        setCustomPairs([]);
                        resetPlayState();
                        alert("Collection deleted successfully!");
                      } catch (error) {
                        console.error("Failed to delete collection:", error);
                        alert("Failed to delete collection. Please try again.");
                      }
                    }
                  }}
                  className="rounded bg-red-500 px-4 py-2 text-white hover:bg-red-600"
                >
                  Delete Selected
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="game-board relative">
          <div className="flex flex-col items-center">
            <p>
              Time Left:{" "}
              {timeLeft === Infinity ? "No limit" : `${timeLeft} seconds`}
            </p>
            <p>Score: {score}</p>
            {feedbackMessage && (
              <p className="mt-2 font-semibold text-red-600">{feedbackMessage}</p>
            )}
            <div className="flex flex-col items-center gap-6 md:flex-row md:justify-center md:gap-20">
              <div className="words-section flex flex-col items-start">
                {customPairs.map((pair) => (
                  <div
                    key={pair.id}
                    draggable={!connections[pair.id]}
                    onDragStart={(event) => handleDragStart(event, pair.id)}
                    onClick={() => handlePromptClick(pair.id)}
                    className={`m-2 w-fit cursor-pointer whitespace-nowrap rounded border border-black px-3 py-2 ${
                      connections[pair.id]
                        ? "bg-green-100 text-black"
                        : selectedPromptId === pair.id
                          ? "bg-yellow-300 text-black"
                          : "bg-white text-black"
                    }`}
                    id={`source-${pair.id}`}
                  >
                    {pair.prompt}
                  </div>
                ))}
              </div>
              <div className="cards-section flex flex-col items-start">
                {shuffledMatches.map((pair) => {
                  const isMatched = Object.values(connections).includes(pair.id);
                  return (
                    <div
                      key={pair.id}
                      className={`m-2 w-fit cursor-pointer whitespace-nowrap rounded border border-black px-3 py-2 ${
                        isMatched ? "bg-green-100 text-black" : "bg-white text-black"
                      }`}
                      onDrop={(event) => handleDrop(event, pair.id)}
                      onDragOver={handleDragOver}
                      onClick={() => handleMatchClick(pair.id)}
                      id={`target-${pair.id}`}
                    >
                      {isImageValue(pair.match) ? (
                        <img
                          src={pair.match}
                          alt="Match item"
                          className="h-16 w-16 object-contain"
                        />
                      ) : (
                        pair.match
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <svg
              className="pointer-events-none absolute left-0 top-0 size-full"
              style={{ zIndex: 1 }}
            >
              {lineCoords.map((coords) => (
                <g key={coords.pairId}>
                  <line
                    x1={coords.x1}
                    y1={coords.y1}
                    x2={coords.x2}
                    y2={coords.y2}
                    stroke="#000"
                    strokeWidth="2"
                    markerEnd="url(#arrowhead-multiple-words)"
                  />
                </g>
              ))}
              <defs>
                <marker
                  id="arrowhead-multiple-words"
                  markerWidth="10"
                  markerHeight="7"
                  refX="9"
                  refY="3.5"
                  orient="auto"
                >
                  <polygon points="0 0, 10 3.5, 0 7" fill="#000" />
                </marker>
              </defs>
            </svg>
          </div>
        </div>
      )}

      <GuidedTour
        steps={tourStepsMultipleWords()}
        isRunning={isTourRunning && isGuidedTourEnabled}
        onComplete={handleTourComplete}
        currentStep={currentTourStep}
        onStepChange={setCurrentTourStep}
        tourName="multipleWords"
      />

      {isGameStarted && isGameCompleted && (
        <div className="flex flex-col items-center">
          <p>Congratulations! You&apos;ve completed the game!</p>
          {isTimedMode && finalCompletionTime !== null && (
            <p>Your completion time: {finalCompletionTime} seconds</p>
          )}
          <button
            type="button"
            onClick={resetGame}
            className="reset-button mt-5 rounded border border-black bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
          >
            Play Again
          </button>
        </div>
      )}
    </div>
  );
};

export default MultipleWordsGame;
